/**
 * Product Card with Optional Image Support
 * Automatically adapts based on tenant settings and available images
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';

export default function ProductCard({ 
  product, 
  onClick, 
  showImages = false,
  imageSize = 'medium',
  dataTestId 
}) {
  const hasImage = product.image_url || product.placeholder_emoji;
  const shouldShowImage = showImages && hasImage;

  // Image size variants
  const sizeClasses = {
    small: 'min-h-[80px]',
    medium: 'min-h-[120px]',
    large: 'min-h-[160px]'
  };

  const imageSizes = {
    small: 'h-12',
    medium: 'h-20',
    large: 'h-28'
  };

  return (
    <button
      onClick={onClick}
      data-testid={dataTestId}
      className={`group h-auto ${shouldShowImage ? sizeClasses[imageSize] : 'min-h-[100px]'} 
                 py-4 px-3 flex flex-col items-start justify-between
                 border-2 border-slate-700/60 bg-gradient-to-br from-[#1A2642]/60 to-[#111C32]/80
                 hover:border-teal-500/60 hover:from-teal-500/10 hover:to-emerald-500/5
                 text-left rounded-xl transition-all duration-200 
                 hover:-translate-y-1 hover:shadow-xl hover:shadow-teal-500/10 
                 active:scale-[0.98] backdrop-blur-sm
                 relative overflow-hidden w-full`}
    >
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-500/0 to-emerald-500/0 
                    group-hover:from-teal-500/5 group-hover:to-emerald-500/5 
                    transition-all duration-300 pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-10 w-full space-y-2">
        
        {/* Product Image or Emoji */}
        {shouldShowImage && (
          <div className="w-full flex justify-center mb-2">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className={`${imageSizes[imageSize]} w-auto object-contain rounded-lg`}
              />
            ) : product.placeholder_emoji ? (
              <div className={`${imageSizes[imageSize]} w-full rounded-lg bg-gradient-to-br from-teal-500/10 to-emerald-500/10 
                            flex items-center justify-center text-4xl`}>
                {product.placeholder_emoji}
              </div>
            ) : null}
          </div>
        )}
        
        {/* Product Name */}
        <span className="font-semibold text-white text-sm sm:text-base line-clamp-2 w-full
                       group-hover:text-teal-100 transition-colors"
              style={{ fontFamily: "'Inter', sans-serif" }}>
          {product.name}
        </span>
        
        {/* Description (only if no image or small image) */}
        {product.description && (!shouldShowImage || imageSize === 'small') && (
          <span className="text-xs text-slate-400 line-clamp-1">
            {product.description}
          </span>
        )}
        
        {/* Price & Badges */}
        <div className="flex items-center justify-between w-full pt-1">
          <span className="text-teal-400 font-bold text-lg sm:text-xl group-hover:text-teal-300 transition-colors"
                style={{ fontFamily: "'Fira Code', monospace" }}>
            {product.price?.toFixed(2)}€
          </span>
          
          {product.has_modifiers && (
            <Badge variant="outline" className="text-xs text-slate-400 border-slate-600 bg-slate-800/50">
              Optionen
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}
